import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Product } from 'shared/types/product/Product';
import { SwiperOptions } from 'swiper';
import Slider from 'components/commercetools-ui/atoms/slider';
import Subtitle, { SubtitleProps } from 'components/commercetools-ui/atoms/subtitle';
import Typography from 'components/commercetools-ui/atoms/typography';
import Wrapper from 'components/HOC/wrapper';
import useClassNames from 'helpers/hooks/useClassNames';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import {
  mobile,
  mobile_large,
  tablet_small,
  tablet_large,
  desktop,
  desktop_xl,
} from 'helpers/utils/preferredScreenSizes';
import { Reference } from 'types/reference';
import { ImageProps } from 'frontastic/lib/image';
import YouMayAlsoLikeTile from './tail';
import useTrack from '../product/product-slider/useTrack';
import ProductTile from '../recentlyViewedProduct/ProductTile';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/24/solid';
import RightIcon from 'public/icons/ag-icons/sliderchevright.svg';
import LeftIcon from 'public/icons/ag-icons/sliderchevleft.svg';
import { usePathname } from 'next/navigation';

export interface YouMayAlsoLikeSliderProps extends Partial<SwiperOptions> {
  products: Product[];
  productLogo?: ImageProps;
  title: string;
  titleVariant?: 'xs' | 'sm' | 'lg';
  subline?: string;
  subtitleVariant?: SubtitleProps['variant'];
  ctaLabel?: string;
  ctaLink?: Reference;
  clearDefaultWrapperStyles?: boolean;
  slidesPerView?: number;
  disableProductQuickView?: boolean;
  disableProductWishlistButton?: boolean;
  disableProductVariants?: boolean;
  innerArrows?: boolean;
  solidArrows?: boolean;
  onProductClick?: (product: Product) => void;
  classNames?: {
    title?: string;
  };
  variant?: 'normal' | 'cart';
}

{
  /*
   * YouMayAlsoLikeSlider Component
   *
   * This component enhances the standard product slider with additional features:
   * - Responsive design for optimal display across devices.
   * - Product information display, including details like name, image, .
   * - Pricing information, allowing users to see the cost of each item.
   * - Size variant selection, enabling users to choose different sizes.
   * - Swatches for color or style variations.
   * - A custom fit button that triggers a specific function  measuring guide).
   * - Wishlist functionality, allowing users to save items for later.
   * - An "Add to Cart" button for seamless purchasing. */
}

const YouMayAlsoLikeSlider: FC<YouMayAlsoLikeSliderProps> = ({
  products,
  title,
  subline,
  titleVariant = 'lg',
  subtitleVariant = 'lg',
  classNames = {},
  ...props
}) => {
  const [startIndex, setStartIndex] = useState(0);
  const [isContentLoaded, setIsContentLoaded] = useState(false);

  const { isTouchDevice } = useTouchDevice();
  const [isMobileLarge] = useMediaQuery(mobile_large);
  const [isTabSmall] = useMediaQuery(tablet_small);
  const [isTabLarge] = useMediaQuery(tablet_large);
  const pathname = usePathname();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isScrolledToStart, setIsScrolledToStart] = useState(true);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);
  const [slideWidth, setSlideWidth] = useState(0);

  const skuMatch = pathname.match(/-(\d+)\.html$/);
  let sku: string;
  if (skuMatch) {
    sku = skuMatch[1];
  }

  const excludedProducts = products?.filter((product) => (product as any)?.slug?.includes(sku));
  const filteredProducts = products?.filter((product) => !(product as any)?.slug?.includes(sku));

  const visibleItems = useMemo(() => {
    if (isTabLarge) {
      return 4;
    } else if (isTabSmall) {
      return 3.5;
    } else if (isMobileLarge) {
      return 2.5;
    }
    return 1.5;
  }, [isTabLarge, isTabSmall, isMobileLarge]);

  const shouldLoop = useMemo(() => {
    if (filteredProducts.length === 1) return false;
    if (filteredProducts.length <= 2) return visibleItems === 1.5; // Infinite loop for default case only
    if (filteredProducts.length === 3) return visibleItems <= 2.5; // Loop for default and istablet
    if (filteredProducts.length === 4) return visibleItems <= 3.5 && !isTabLarge; // Loop for default, istablet, and isDesktopSize
    return !isTabLarge; // Infinite loop for all screen sizes below isMediumDesktopSize
  }, [filteredProducts.length, visibleItems, isTabLarge]);

  useEffect(() => {
    setIsContentLoaded(true);
  }, []);

  useEffect(() => {
    if (scrollContainerRef.current) {
      setSlideWidth(scrollContainerRef.current.offsetWidth / visibleItems);
    }
  }, [visibleItems]);

  // Scroll event handler to track scrollbar position
  const handleScroll = () => {
    if (!scrollContainerRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    const isAtStart = scrollLeft <= 5;
    const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 5;

    setIsScrolledToStart(isAtStart);
    setIsScrolledToEnd(isAtEnd);

    const newStartIndex = Math.round(scrollLeft / slideWidth);
    setStartIndex(newStartIndex);
  };

  // Scroll Left with Smooth Behavior
  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -slideWidth,
        behavior: 'smooth', // Enable smooth scrolling
      });
    }
  };

  // Scroll Right with Smooth Behavior
  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: slideWidth,
        behavior: 'smooth', // Enable smooth scrolling
      });
    }
  };

  // Attach scroll event listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      handleScroll(); // Run initially to set correct button states
    }
    return () => {
      container?.removeEventListener('scroll', handleScroll);
    };
  }, [visibleItems, slideWidth]);

  const renderItems = (items: Product[]) => {
    const itemWidth = 100 / visibleItems;
    if (shouldLoop) {
      const loopedItems = [...items, ...items, ...items]; // Create a looped list
      const start = items.length + startIndex; // Offset for the middle section
      const end = start + Math.ceil(visibleItems);
      return loopedItems.slice(start, end).map((item, index) => (
        <div
          key={index}
          className="flex flex-shrink-0 items-stretch transition-transform duration-300"
          style={{ width: `${itemWidth}%`, minWidth: `${itemWidth}%` }}
        >
          <ProductTile product={item} />
        </div>
      ));
    }

    // return items.slice(startIndex, startIndex + Math.ceil(visibleItems)).map((item, index) => (
    return items.map((item, index) => (
      <div
        key={index}
        className="flex flex-shrink-0 items-stretch transition-transform duration-300"
        style={{
          width: `${itemWidth}%`,
          minWidth: `${itemWidth}%`,
          flex: `0 0 calc(100% / ${visibleItems})`,
          scrollSnapAlign: 'start',
        }}
      >
        <ProductTile product={item} />
      </div>
    ));
  };

  // const handleLeftClick = () => {
  //   setStartIndex((prevIndex) =>
  //     shouldLoop ? (prevIndex - 1 + filteredProducts?.length) % filteredProducts?.length : Math.max(0, prevIndex - 1),
  //   );
  // };

  // const handleRightClick = () => {
  //   setStartIndex((prevIndex) =>
  //     shouldLoop
  //       ? (prevIndex + 1) % filteredProducts?.length
  //       : Math.min(filteredProducts?.length - Math.ceil(visibleItems), prevIndex + 1),
  //   );
  // };

  if (!isContentLoaded) {
    return null;
  }

  return (
    <Wrapper clearDefaultStyles className="my-30 max-w-[104rem] xl:mx-50 2xl:mx-auto">
      <div
        className={`flex items-center ${
          isTabSmall || filteredProducts?.length === 1 ? 'relative justify-center' : 'justify-between'
        }`}
      >
        <Typography
          className="align-center mx-20 mb-30 flex justify-center text-center text-16 font-bold leading-7 md:text-24 lg:mx-0"
          as="h3"
        >
          {title}
        </Typography>
        {subline && (
          <div className="mt-8 flex items-center justify-between md:mt-16 lg:mt-14">
            <Subtitle subtitle={subline} variant={subtitleVariant} />
          </div>
        )}
        {filteredProducts?.length > visibleItems && (
          <div className={`mb-20 flex items-center justify-end gap-x-6 ${isTabSmall ? 'absolute right-[2%]' : ''}`}>
            <button
              className={`border-1 flex h-[48px] w-[48px] items-center justify-center rounded-full border bg-white p-3 text-gray-700 ${
                startIndex === 0 && !shouldLoop && isScrolledToStart
                  ? 'cursor-not-allowed border-[#E7E7E7] opacity-50'
                  : 'border-black'
              }`}
              onClick={handleScrollLeft}
              disabled={!shouldLoop && startIndex === 0 && isScrolledToStart}
              aria-disabled={isScrolledToStart}
            >
              <LeftIcon />
            </button>
            <button
              className={`border-1 flex h-[48px] w-[48px] items-center justify-center rounded-full border bg-white p-3 text-gray-700 ${
                startIndex >= filteredProducts?.length - Math.ceil(visibleItems) && !shouldLoop && isScrolledToEnd
                  ? 'cursor-not-allowed border-[#E7E7E7] opacity-50'
                  : 'border-black'
              }`}
              // onClick={handleScrollRight}
              onClick={handleScrollRight}
              disabled={
                !shouldLoop && startIndex >= filteredProducts?.length - Math.ceil(visibleItems) && isScrolledToEnd
              }
              aria-disabled={isScrolledToEnd}
            >
              <RightIcon />
            </button>
          </div>
        )}
      </div>
      <div
        ref={scrollContainerRef}
        className={`ml-10 ${
          filteredProducts?.length === 1 ? 'justify-center sm:justify-start 2xl:ml-[4rem]' : ''
        } flex items-stretch overflow-hidden md:ml-0 ${
          filteredProducts.length > 4
            ? 'ag__cc__wrapper flex justify-around overflow-x-auto hover:overflow-x-scroll'
            : 'justify-start 2xl:ml-[4rem]'
        } gap-5`}
        style={{
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          overflowY: 'hidden',
          paddingBottom: '20px',
        }}
      >
        {renderItems(filteredProducts)}
      </div>
    </Wrapper>
  );
};

export default YouMayAlsoLikeSlider;
